import { useState, useEffect } from "react";
import { Link, useLocation, useRouter } from "@tanstack/react-router";
import {
  LayoutDashboard,
  CalendarDays,
  Users,
  UserRound,
  UserCheck,
  Building2,
  LogOut,
  BriefcaseMedical,
  PackageOpen,
  ChevronDown,
  ChevronRight,
  Box,
  ShoppingCart,
  Tags,
  FlaskConical,
  Megaphone,
  Bike,
  BoxesIcon,
  FileText,
  SquarePlay,
  List,
  FileSpreadsheet,
  BarChart3,
  Search,
  PackageCheck,
  Receipt,
  Star,
} from "lucide-react";
import { NavigationItem, User } from "../../types/auth";
import { Logo } from "./Logo";
import { AuthService } from "../../services/api/auth.service";

export function Sidebar({ onLogout }: { onLogout: () => void }) {
  const [user, setUser] = useState<User | null>(null);
  const [openSubmenus, setOpenSubmenus] = useState<Set<string>>(new Set());
  const router = useRouter();
  const location = useLocation();
  const activePath = location.pathname;

  const toggleSubmenu = (menuName: string) => {
    setOpenSubmenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(menuName)) {
        newSet.delete(menuName);
      } else {
        newSet.add(menuName);
      }
      return newSet;
    });
  };

  // Determine base path based on user role
  const userRole = user?.role?.toLowerCase();
  const basePath =
    userRole === "admin"
      ? "/admin"
      : userRole === "medicalcamp"
        ? "/medicalcamp"
        : userRole === "lab"
          ? "/lab"
          : "/clinic";

  const isAdmin = userRole === "admin";
  const isMedicalCamp = userRole === "medicalcamp";
  const isClinic = userRole === "clinic";
  const isLab = userRole === "lab";

  // Build navigation items based on user role

  const navigation: NavigationItem[] = [
    ...(!isLab
      ? [
        {
          name: "Overview",
          href: `${basePath}/dashboard`,
          icon: LayoutDashboard,
        },
      ]
      : []),

    // Only non-admin roles have appointments
    ...(!isAdmin && !isLab
      ? [
        {
          name: "Appointments",
          href: `${basePath}/appointments`,
          icon: CalendarDays,
        }
      ]
      : []),

    // All roles have doctors and patients
    ...(!isLab
      ? [
        {
          name: "Appointment Overview",
          href: `${basePath}/appointments/overview`,
          icon: CalendarDays,
        },
        { name: "Doctors", href: `${basePath}/doctors`, icon: UserRound },
        { name: "Patients", href: `${basePath}/patients`, icon: Users },
        { name: "Staff", href: `${basePath}/staff`, icon: UserCheck },
        {
          name: "Driver",
          href: `${basePath}/stocks-orders/delivery-boys`,
          icon: Bike,
        },
      ]
      : []),

    ... (!isLab && !isMedicalCamp
      ? [
        {
          name: "Reports",
          icon: FileSpreadsheet,
          subItems: [
            {
              name: "Medicine Order",
              href: `${basePath}/reports/medicine-order`,
              icon: FileSpreadsheet,
            },
            {
              name: "Labtest Order",
              href: `${basePath}/reports/labtest-order`,
              icon: FileSpreadsheet,
            },
            {
              name: "Appointment",
              href: `${basePath}/reports/appointment`,
              icon: FileSpreadsheet,
            },
            {
              name: "Daywise",
              href: `${basePath}/reports/daywise`,
              icon: FileSpreadsheet,
            },
            {
              name: "Mft/Mkt Report",
              href: `${basePath}/reports/stock-report`,
              icon: FileSpreadsheet,
            },
            ...(isAdmin
              ? [
                  {
                    name: "Available Stock",
                    href: `${basePath}/reports/available-stock`,
                    icon: FileSpreadsheet,
                  },
                ]
              : []),
          ],
        },
      ]
      : []),

    // Only admin has additional items
    ...(isAdmin
      ? [

        { name: "Clinics", href: `${basePath}/clinics`, icon: Building2 },
        {
          name: "MedicalCamp",
          href: `${basePath}/medicalcamp`,
          icon: BriefcaseMedical,
        },
        {
          name: "Blogs",
          href: `${basePath}/blogs`,
          icon: FileText,
        },
        {
          name: "Video-Blogs",
          href: `${basePath}/video-blogs`,
          icon: SquarePlay,
        },
        {
          name: "Analytics",
          icon: BarChart3,
          subItems: [
            {
              name: "Search Analytics",
              href: `${basePath}/analytics/search-analytics`,
              icon: Search,
            },
            {
              name: "Product Enquiry",
              href: `${basePath}/analytics/product-enquiry`,
              icon: PackageCheck,
            },
            {
              name: "Review Analytics",
              href: `${basePath}/analytics/review-analytics`,
              icon: Star,
            },
          ],
        },
        // {
        //   name: "Order Management",
        //   href: `${basePath}/order-management`,
        //   icon: List,
        // },
        {
          name: "Manage Orders",
          icon: BoxesIcon,
          subItems: [
            {
              name: "Dashboard",
              href: `${basePath}/stocks-orders/dashboard`,
              icon: LayoutDashboard,
            },
            {
              name: "Inventory Mgmt",
              href: `${basePath}/stocks-orders/inventory-management`,
              icon: FileText,
            },
            // {
            //   name: "Orders",
            //   href: `${basePath}/stocks-orders/orders`,
            //   icon: ShoppingCart,
            // },
            {
              name: "Orders",
              icon: List,
              subItems: [
                {
                  name: "Online",
                  href: `${basePath}/stocks-orders/online-orders`,
                  icon: ShoppingCart,
                },
                {
                  name: "Offline",
                  href: `${basePath}/stocks-orders/offline-orders`,
                  icon: ShoppingCart,
                },
                {
                  name: "Labtest",
                  href: `${basePath}/stocks-orders/labtest-orders`,
                  icon: FlaskConical,
                },
                {
                  name: "Offline Labtest",
                  href: `${basePath}/stocks-orders/offline-labtest-orders`,
                  icon: FlaskConical,
                },
              ],
            },
            {
              name: "Products",
              href: `${basePath}/stocks-orders/products`,
              icon: Box,
            },
            {
              name: "Lab Tests",
              href: `${basePath}/stocks-orders/lab-tests`,
              icon: FlaskConical,
            },
          ],
        },
        {
          name: "Transaction History",
          href: `${basePath}/transaction-history`,
          icon: Receipt,
        },
      ]
      : isClinic
        ? [
          {
            name: "Manage Orders",
            icon: BoxesIcon,
            subItems: [
              {
                name: "Dashboard",
                href: `${basePath}/stocks-orders/dashboard`,
                icon: LayoutDashboard,
              },
              // {
              //   name: "Orders",
              //   href: `${basePath}/stocks-orders/orders`,
              //   icon: ShoppingCart,
              // },
              {
                name: "Order",
                icon: ShoppingCart,
                subItems: [
                  {
                    name: "Online",
                    href: `${basePath}/stocks-orders/online-orders`,
                    icon: ShoppingCart,
                  },
                  {
                    name: "Offline",
                    href: `${basePath}/stocks-orders/offline-orders`,
                    icon: ShoppingCart,
                  },
                  {
                    name: "Labtest",
                    href: `${basePath}/stocks-orders/labtest-orders`,
                    icon: FlaskConical,
                  },
                  {
                    name: "Offline Labtest",
                    href: `${basePath}/stocks-orders/offline-labtest-orders`,
                    icon: FlaskConical,
                  },
                ],
              },
              // { name: "Products", href: `${basePath}/stocks-orders/products`, icon: Box },
              {
                name: "Inventory",
                href: `${basePath}/stocks-orders/inventory-management`,
                icon: FlaskConical,
              },
              {
                name: "Lab Tests",
                href: `${basePath}/stocks-orders/lab-tests`,
                icon: FlaskConical,
              },
            ],
          },
          {
            name: "Transaction History",
            href: `${basePath}/transaction-history`,
            icon: Receipt,
          },
        ]
        : isLab
          ? [
            {
              name: "Lab Test",
              icon: FlaskConical,
              href: `${basePath}/dashboard`,
            },
          ]
          : []),
  ];

  const handleLogout = () => {
    onLogout();

    // Redirect to appropriate login page based on role
    const loginPath = isAdmin
      ? "/admin/login"
      : isMedicalCamp
        ? "/medicalcamp/login"
        : isLab
          ? "/lab/login"
          : "/clinic/login";

    router.navigate({ to: loginPath });
  };

  // Determine display title for user role
  const roleDisplay = isAdmin
    ? "Admin"
    : isMedicalCamp
      ? "Medical Camp Admin"
      : isLab
        ? "Lab Admin"
        : "Clinic Admin";

  useEffect(() => {
    AuthService.fetchUserDetails()
      .then((userData) => {
        if (userData) {
          setUser(userData);
        } else {
          console.warn("User data is null!");
        }
      })
      .catch((error) => {
        console.error("Error fetching user:", error);
      });
  }, []);

  if (!user) return <div>Loading...</div>;

  const renderNavigationItem = (item: NavigationItem) => {
    const isItemActive = (href?: string) => Boolean(href && activePath === href);
    const isChildActive = Boolean(
      item.subItems?.some(
        (sub) => sub.href && (activePath === sub.href || activePath.startsWith(sub.href))
      )
    );

    if (item.subItems) {
      return (
        <div key={item.name}>
          <button
            onClick={() => toggleSubmenu(item.name)}
            className={`flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-gray-700 rounded-lg transition-colors
              ${isChildActive ? "text-[#771F52] bg-[#771F52]/5" : "hover:bg-gray-50"}
            `}
          >
            <div className="flex items-center gap-3">
              <item.icon
                className={`w-5 h-5 ${isChildActive ? "text-[#771F52]" : "text-gray-500"}`}
              />
              {item.name}
            </div>
            {openSubmenus.has(item.name) ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>

          {openSubmenus.has(item.name) && (
            <div className="p-3 mt-1 space-y-1 bg-inherit rounded-lg flex flex-col gap-2 ml-4">
              {item.subItems.map((subItem) => renderNavigationItem(subItem))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.name}
        to={item.href || "#"}
        className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
          isItemActive(item.href)
            ? "text-red-800 bg-red-50"
            : "text-gray-700 hover:text-red-800 hover:bg-red-50"
        }`}
      >
        {item.icon && (
          <item.icon
            className={`w-5 h-5 ${isItemActive(item.href) ? "text-red-800" : ""}`}
          />
        )}
        {item.name}
      </Link>
    );
  };

  return (
    <div className="flex flex-col h-full bg-white border-r">
      <div className="p-6">
        <Logo />
      </div>

      <nav className="flex-1 px-4 space-y-1 overflow-y-auto hiddenScrollbar">
        {navigation.map((item) => renderNavigationItem(item))}
      </nav>

      <div className="p-4 border-t">
        <div className="flex items-center gap-3 px-3 py-2 mb-2">
          <div className="relative w-8 h-8 rounded-full overflow-hidden">
            <img
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                user.name
              )}`}
              alt={user.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900">{user.name}</div>
            <div className="text-xs text-gray-500 capitalize">
              {roleDisplay}
            </div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-3 py-2 text-sm font-medium text-gray-700 rounded-lg hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          Logout
        </button>
      </div>
    </div>
  );
}
